// Hooks über dem Entitlements-Objekt aus dem PaymentContext. Alle Sperren im
// Dashboard lesen nur diese Hooks. Logik und Zählung liegen in den reinen
// Modulen (entitlements.js, limits.js, usage/*), hier steht nur React.
//
// Geladen wird Nutzung nur, wenn sie zählt. useLimit mit fetch 'auto' lädt nur
// in enforced, Bestandskunden bekommen also keinen zusätzlichen Request. Guards
// zählen vor der Entscheidung frisch und sind bei Zählfehlern fail-open.
// Dialoge öffnen sie nicht, sie liefern ein GateResult. Den Dialog-Host gibt
// es ab PR 3.
//
// Kein Lesen importierter Werte auf Modulebene. usage-queries.js zieht
// use-talents-query.js und damit context/index.js, bei einem Import-Zyklus
// wären importierte Konstanten beim Laden noch undefined.
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo } from 'react'

import {
  usePartnerContext,
  usePaymentContext,
  useUserContext
} from '../../context'
import { LEGACY_ENTITLEMENTS, MODES } from './entitlements'
import { LIMIT_STATES, canAdd, resolveLimitState } from './limits'
import { FEATURES } from './plans'
import {
  careerUsageQueryOptions,
  countUsage,
  seatUsageQueryOptions,
  talentStorageUsageQueryOptions,
  usageQueryKeyPrefix
} from './usage-queries'
import { CAREER_SLOT_ACTIONS, careerSlotDelta } from './usage/careers'
import {
  lockedIdsFor,
  lockedUnitsFor,
  rankTalentStorageCached,
  talentRecoverDelta,
  talentRecoverLockedVisible
} from './usage/talent-storage'

export const GATE_REASONS = Object.freeze({
  LIMIT_REACHED: 'LIMIT_REACHED',
  COUNT_FAILED: 'COUNT_FAILED'
})

export const TALENT_LOCK_STATUS = Object.freeze({
  PENDING: 'pending',
  LOCKED: 'locked',
  UNLOCKED: 'unlocked'
})

const USAGE_RESOURCES = ['careers', 'seats', 'talentStorage']

const EMPTY_SET = new Set()
const EMPTY_INDEX = Object.freeze({
  orderedIds: Object.freeze([]),
  rankById: new Map(),
  orderedUnits: Object.freeze([]),
  rankByUnit: new Map(),
  unitById: new Map(),
  used: 0
})

const isSetKey = (value) =>
  value !== null && value !== undefined && value !== ''

// IDs und owner der rohen Talents-Daten, gezählt oder nicht. Einmal je
// Datenstand, der Query-Cache liefert bei gleichen Daten dieselbe
// Array-Referenz.
const EMPTY_KNOWN = Object.freeze({ ids: EMPTY_SET, owners: EMPTY_SET })
const knownCache = new WeakMap()

const knownOf = (spaces) => {
  if (!Array.isArray(spaces)) return EMPTY_KNOWN

  const cached = knownCache.get(spaces)
  if (cached) return cached

  const known = { ids: new Set(), owners: new Set() }
  spaces.forEach((space) => {
    if (isSetKey(space?.id)) known.ids.add(space.id)
    if (isSetKey(space?.owner)) known.owners.add(space.owner)
  })
  knownCache.set(spaces, known)

  return known
}

// Merker je Query-Objekt der Talents-Query, geteilt von allen Hooks. Für jede
// ID und jeden owner, der in den Daten fehlte, steht dort die Zahl der Fetches
// beim ersten Fehlen, nach einem Fetch ohne ihn ABSENT. KNOWN heißt, er stand
// schon in den Daten. Fehlt er danach, hat ein Fetch ihn schon nicht mehr
// geliefert (etwa gelöscht), dann gilt er sofort als ABSENT. requestedAt ist
// die Zahl der Fetches beim letzten angestoßenen Nachladen. Ein neues
// Query-Objekt (neuer QueryClient, gc) beginnt leer.
const ABSENT = -1
const KNOWN = -2
const missingCache = new WeakMap()

const missingOf = (cachedQuery) => {
  if (!cachedQuery) return null

  let missing = missingCache.get(cachedQuery)
  if (!missing) {
    missing = { ids: new Map(), owners: new Map(), requestedAt: null }
    missingCache.set(cachedQuery, missing)
  }

  return missing
}

const hasOwn = (object, key) =>
  !!object && Object.prototype.hasOwnProperty.call(object, key)

const toList = (value) => {
  if (Array.isArray(value)) return value
  return value ? [value] : []
}

// Laut melden, aber je Key nur einmal pro Seitenaufruf. console.error mit
// einer Error-Instanz geht über src/index.js an Bugsnag.
const reportedUnknown = new Set()

const reportUnknown = (kind, key) => {
  const id = `${kind}:${String(key)}`
  if (reportedUnknown.has(id)) return
  reportedUnknown.add(id)
  console.error(new Error(`pricing ${kind}: unbekannter Key ${String(key)}`))
}

// fetch false lädt nie und liest nur den Cache. true lädt bei jedem
// Plan-Payment (preview und enforced), etwa für Plan & Billing. 'auto' lädt
// nur in enforced.
const shouldFetchUsage = (fetch, mode) => {
  if (fetch === false) return false
  if (fetch === true) return mode !== MODES.LEGACY
  return mode === MODES.ENFORCED
}

// idle heißt, es wird nicht geladen und es gibt keine Daten
const statusOf = (query, enabled) => {
  if (!query) return 'idle'
  if (query.status === 'pending') return enabled ? 'pending' : 'idle'
  return query.status
}

const usePartnerId = () => usePartnerContext().partner?.id

const useUsername = () => useUserContext().cognitoUser?.username

/** Entitlements aus dem PaymentContext, ohne sie LEGACY_ENTITLEMENTS */
export const useEntitlements = () =>
  usePaymentContext().entitlements ?? LEGACY_ENTITLEMENTS

// Für unbekannte Keys. Frei wie in legacy, damit ein Tippfehler nichts sperrt.
const unknownFeatureState = (key) => ({
  key,
  product: null,
  products: [],
  catalogProducts: [],
  i18nKey: null,
  legacyKey: null,
  includedIn: [],
  addonIn: [],
  minPlan: null,
  enabled: true,
  locked: false,
  inPlan: null,
  gated: false,
  reason: null,
  source: 'legacy',
  mode: MODES.LEGACY
})

/**
 * Zustand eines Features (Keys aus FEATURES in plans.js) plus mode. Ein
 * unbekannter Key geht an console.error und ist frei wie in legacy.
 *
 * @param {string} key
 */
export const useFeature = (key) => {
  const entitlements = useEntitlements()
  const known = hasOwn(FEATURES, key)

  useEffect(() => {
    if (!known) reportUnknown('feature', key)
  }, [known, key])

  const feature = known
    ? (entitlements.features?.[key] ?? LEGACY_ENTITLEMENTS.features[key])
    : null
  const { mode } = entitlements

  return useMemo(
    () => (feature ? { ...feature, mode } : unknownFeatureState(key)),
    [feature, mode, key]
  )
}

/**
 * Limit-Zustand einer Ressource (careers, seats, talentStorage) mit der
 * Nutzung aus dem Query-Cache.
 *
 * Alle drei Nutzungs-Queries werden immer aufgerufen (Rules of Hooks), aktiv
 * ist nur die der Ressource. Mit fetch false wird nur der Cache gelesen.
 *
 * @param {'careers'|'seats'|'talentStorage'} resource
 * @param {{ fetch?: 'auto'|boolean }} [options]
 * @returns {object} LimitState plus status ('idle'|'pending'|'success'|
 *   'error'), isFetching und canAdd(n = 1)
 */
export const useLimit = (resource, { fetch = 'auto' } = {}) => {
  const entitlements = useEntitlements()
  const partnerId = usePartnerId()
  const username = useUsername()
  const queryClient = useQueryClient()

  const base = entitlements.limits?.[resource] ?? null

  useEffect(() => {
    if (!base) reportUnknown('limit', resource)
  }, [base, resource])

  const fetchUsage =
    !!base && !!partnerId && shouldFetchUsage(fetch, entitlements.mode)
  const context = { partnerId, username, queryClient }

  const careers = useQuery({
    ...careerUsageQueryOptions(context),
    enabled: fetchUsage && resource === 'careers'
  })
  const seats = useQuery({
    ...seatUsageQueryOptions(context),
    enabled: fetchUsage && resource === 'seats'
  })
  const talentStorage = useQuery({
    ...talentStorageUsageQueryOptions(context),
    enabled: fetchUsage && resource === 'talentStorage' && !!username
  })

  const query = { careers, seats, talentStorage }[resource] ?? null
  const enabled =
    fetchUsage && (resource !== 'talentStorage' || !!username) && !!query
  const used = query ? countUsage(resource, query.data) : null
  const status = statusOf(query, enabled)
  const isFetching = !!query?.isFetching

  const state = useMemo(
    () =>
      resolveLimitState({
        ...(base || { limit: null, enforced: false, source: null }),
        resource,
        used
      }),
    [base, resource, used]
  )

  return useMemo(
    () => ({
      ...state,
      status,
      isFetching,
      canAdd: (n = 1) => canAdd(state, n)
    }),
    [state, status, isFetching]
  )
}

// GateResult, das alle Guards liefern. delta ist null, solange nicht gezählt
// wurde.
const gateResult = ({
  allowed = true,
  enforced = false,
  resource,
  used = null,
  limit = null,
  delta = null,
  state = null,
  reason = null
}) => ({ allowed, enforced, resource, used, limit, delta, state, reason })

// Frisch zählen und entscheiden. staleTime 0 erzwingt einen Request, laufende
// Requests teilt react-query. retry false, sonst greift der globale Default
// aus src/index.js mit bis zu drei Versuchen, und der Guard hinge Sekunden.
// Ohne Zählung (Fehler oder keine Daten) ist der Guard fail-open.
const checkWithFreshCount = async ({
  queryClient,
  resource,
  base,
  options,
  deltaOf,
  blockedOf
}) => {
  if (!base?.enforced) {
    return gateResult({
      resource,
      limit: base?.limit ?? null,
      state: base?.state ?? null
    })
  }

  if (base.state === LIMIT_STATES.UNLIMITED) {
    return gateResult({
      enforced: true,
      resource,
      limit: base.limit,
      state: base.state
    })
  }

  let data
  try {
    data = await queryClient.fetchQuery({
      ...options,
      staleTime: 0,
      retry: false
    })
  } catch {
    data = null
  }

  const used = countUsage(resource, data)
  if (used === null) {
    return gateResult({
      enforced: true,
      resource,
      limit: base.limit,
      state: base.state,
      reason: GATE_REASONS.COUNT_FAILED
    })
  }

  const delta = deltaOf(data)
  const state = resolveLimitState({ ...base, used })
  // blockedOf ist ein zweiter Grund zu blocken, auch wenn ein Platz frei wäre
  const allowed =
    canAdd(state, delta) &&
    !(typeof blockedOf === 'function' && blockedOf(data, state))

  return gateResult({
    allowed,
    enforced: true,
    resource,
    used: state.used,
    limit: state.limit,
    delta,
    state: state.state,
    reason: allowed ? null : GATE_REASONS.LIMIT_REACHED
  })
}

/**
 * Guard für Stellen. checkActivation für Aktivieren, Duplizieren mit
 * Aktivierung und Discover, checkRecover für Wiederherstellen. Beide nehmen
 * eine Stelle oder eine Liste und zählen frisch über die leichte
 * Stellen-Query, nie über useCareersQuery. Eine Ersetzung per
 * external_custom_id kostet keinen Slot (careerSlotDelta).
 *
 * @returns {{ checkActivation(careerOrCareers): Promise<object>,
 *   checkRecover(careers): Promise<object> }}
 */
export const useCareerGate = () => {
  const { limits } = useEntitlements()
  const base = limits?.careers ?? null
  const partnerId = usePartnerId()
  const queryClient = useQueryClient()

  const check = useCallback(
    (action, careers) =>
      checkWithFreshCount({
        queryClient,
        resource: 'careers',
        base,
        options: careerUsageQueryOptions({ partnerId }),
        deltaOf: (activeCareers) =>
          careerSlotDelta({ action, careers, activeCareers })
      }),
    [queryClient, base, partnerId]
  )

  const checkActivation = useCallback(
    (careerOrCareers) => check(CAREER_SLOT_ACTIONS.ACTIVATE, careerOrCareers),
    [check]
  )
  const checkRecover = useCallback(
    (careers) => check(CAREER_SLOT_ACTIONS.RECOVER, careers),
    [check]
  )

  return useMemo(
    () => ({ checkActivation, checkRecover }),
    [checkActivation, checkRecover]
  )
}

/**
 * Guard für Einladungen ins Team. Zählt die Zugänge frisch über die Query der
 * Mitgliederliste, eine Einladung belegt einen Zugang.
 *
 * @returns {{ checkInvite(): Promise<object> }}
 */
export const useSeatGuard = () => {
  const { limits } = useEntitlements()
  const base = limits?.seats ?? null
  const partnerId = usePartnerId()
  const queryClient = useQueryClient()

  const checkInvite = useCallback(
    () =>
      checkWithFreshCount({
        queryClient,
        resource: 'seats',
        base,
        options: seatUsageQueryOptions({ partnerId }),
        deltaOf: () => 1
      }),
    [queryClient, base, partnerId]
  )

  return useMemo(() => ({ checkInvite }), [checkInvite])
}

/**
 * Talentspeicher mit Rang und Sperren, berechnet auf den rohen Daten der
 * Talents-Query (gleicher Cache wie die Talentliste). Gezählt wird je Person
 * (owner, ohne owner je Space), used ist die Zahl der Personen.
 *
 * Gesperrt wird nur in enforced, ab Person N+1, dann mit allen ihren Spaces.
 * rankOf(id) liefert den Rang der Person zu einer Space-ID, Spaces derselben
 * Person teilen ihn. Vorläufig (provisional) ist der Zustand, solange die
 * Entitlements nicht settled sind und der Kunde ein Plan-Payment hat oder
 * das Plan-Feld noch lädt. Während das Plan-Feld lädt,
 * ist ein Plan-Payment nicht erkennbar und mode steht auf legacy, deshalb
 * zählt das mit. Ebenso offen ist es in enforced mit endlichem Limit, solange
 * die Talents-Query keine Daten hat (uncounted). In beiden Fällen ist pending
 * true, status 'pending' und isLocked gilt für jede ID, damit keine echten
 * Ergebnisse aufblitzen. Ein Query-Fehler entsperrt (fail-open).
 *
 * Gesperrt wird je Person, also auch jeder Space einer gesperrten Person, der
 * selbst nicht zählt (etwa ATTRACT ohne mail_stored), ausgenommen
 * Beispieldaten und Demo. Für Spaces, die gar nicht in den Daten stehen
 * (Mitarbeitende), prüfen Aufrufer mit owner zusätzlich isOwnerLocked.
 * rankOfOwner liefert den Rang einer Person.
 *
 * Dazu kommt pending je ID (isPendingId) und je owner (isPendingOwner). Fehlt
 * eine ID in den Daten, kennt der Cache sie vielleicht nur noch nicht, etwa
 * ein neues Talent hinter einem persistierten oder noch frischen Stand. Sie
 * gilt als gesperrt und pending, bis die Talents-Query nach dem ersten Fehlen
 * einmal fertig geladen hat. Läuft gerade kein Fetch, stößt der Hook ihn an
 * (nur, wenn ein Observer die Query aktiv hält). Fehlt sie danach weiter, ist
 * sie nicht gezählt (etwa Mitarbeitende) und bleibt frei, auch bei späteren
 * Aktualisierungen, sonst flackerte jede Hintergrund-Aktualisierung. Ein
 * Fehler beim Nachladen entsperrt (fail-open). IDs in den Daten, die nicht
 * zählen und zu keiner gezählten Person gehören, sind sofort frei. Für owner
 * gilt dasselbe. Der Merker hängt am Query-Objekt und gilt für alle Hooks.
 *
 * @param {{ fetch?: boolean|'auto' }} [options] false liest nur den Cache
 */
export const useTalentStorage = ({ fetch = false } = {}) => {
  const entitlements = useEntitlements()
  const partnerId = usePartnerId()
  const username = useUsername()
  const queryClient = useQueryClient()

  const base = entitlements.limits?.talentStorage ?? null
  const provisional =
    !entitlements.settled &&
    (!!entitlements.planPayment || entitlements.planFieldSettled === false)
  const enabled =
    !!partnerId && !!username && shouldFetchUsage(fetch, entitlements.mode)

  const options = talentStorageUsageQueryOptions({
    partnerId,
    username,
    queryClient
  })
  const query = useQuery({ ...options, enabled })

  const spaces = query.data?.[0]
  const hasData = Array.isArray(spaces)
  const index = hasData ? rankTalentStorageCached(spaces) : EMPTY_INDEX
  const used = hasData ? index.used : null
  const enforced = !!base?.enforced
  const limit = base?.limit ?? null
  const uncounted =
    enforced && Number.isFinite(limit) && !hasData && query.status !== 'error'
  const pending = provisional || uncounted

  const state = useMemo(
    () =>
      resolveLimitState({
        ...(base || { limit: null, enforced: false, source: null }),
        resource: 'talentStorage',
        used
      }),
    [base, used]
  )

  const lockedIds = useMemo(() => {
    if (provisional) return new Set(index.rankById.keys())
    if (!enforced) return EMPTY_SET
    return lockedIdsFor(index, limit)
  }, [provisional, enforced, index, limit])

  const lockedUnits = useMemo(() => {
    if (provisional) return new Set(index.orderedUnits)
    if (!enforced) return EMPTY_SET
    return lockedUnitsFor(index, limit)
  }, [provisional, enforced, index, limit])

  const status = pending ? 'pending' : statusOf(query, enabled)
  const isFetching = !!query.isFetching

  const rankOf = useCallback((id) => index.rankById.get(id) ?? null, [index])
  const rankOfOwner = useCallback(
    (owner) => (isSetKey(owner) ? (index.rankByUnit.get(owner) ?? null) : null),
    [index]
  )

  const known = knownOf(spaces)
  const lockActive = enforced && Number.isFinite(limit)

  // Fertige Fetches (Daten oder Fehler) der Talents-Query. Zählt auch ein
  // Fetch, dessen Daten gleich geblieben sind.
  const { queryKey } = options
  const cachedQuery = queryClient.getQueryCache().find({ queryKey })
  const missing = missingOf(cachedQuery)
  const fetchCount =
    (cachedQuery?.state.dataUpdateCount ?? 0) +
    (cachedQuery?.state.errorUpdateCount ?? 0)

  // Einmal je Datenstand nachladen, nach dem Render. refetchQueries lässt
  // eine Query aus, die nur abgeschaltete Observer hat (fetch false).
  const requestRefetch = useCallback(() => {
    if (!missing || missing.requestedAt === fetchCount) return
    missing.requestedAt = fetchCount
    Promise.resolve().then(() =>
      queryClient.refetchQueries(
        { queryKey: cachedQuery.queryKey, exact: true },
        { cancelRefetch: false }
      )
    )
  }, [missing, fetchCount, queryClient, cachedQuery])

  const isPendingKey = useCallback(
    (key, knownKeys, missingKeys) => {
      if (!lockActive || !isSetKey(key) || !missingKeys) return pending
      if (hasData && knownKeys.has(key)) {
        if (missingKeys.get(key) !== KNOWN) missingKeys.set(key, KNOWN)
        return pending
      }

      // Vor den Daten merken, dann reicht der erste Fetch. Nach einem
      // Zurücksetzen der Query zählt der neue Stand.
      const firstMissing = missingKeys.get(key)
      if (firstMissing === KNOWN) {
        if (hasData) missingKeys.set(key, ABSENT)
      } else if (firstMissing === undefined || firstMissing > fetchCount) {
        missingKeys.set(key, fetchCount)
      }
      if (pending) return true
      if (!hasData || missingKeys.get(key) === ABSENT) return false

      if (!isFetching && fetchCount > missingKeys.get(key)) {
        missingKeys.set(key, ABSENT)
        return false
      }
      if (!isFetching) requestRefetch()
      return true
    },
    [pending, lockActive, hasData, isFetching, fetchCount, requestRefetch]
  )

  const isPendingId = useCallback(
    (id) => isPendingKey(id, known.ids, missing?.ids),
    [isPendingKey, known, missing]
  )
  const isPendingOwner = useCallback(
    (owner) => isPendingKey(owner, known.owners, missing?.owners),
    [isPendingKey, known, missing]
  )

  // isPendingId zuerst, so merkt es sich jede ID, die in den Daten stand
  const isLocked = useCallback(
    (id) => isPendingId(id) || lockedIds.has(id),
    [lockedIds, isPendingId]
  )
  const isOwnerLocked = useCallback(
    (owner) =>
      isPendingOwner(owner) || (isSetKey(owner) && lockedUnits.has(owner)),
    [lockedUnits, isPendingOwner]
  )

  return useMemo(
    () => ({
      status,
      isFetching,
      enforced,
      provisional,
      pending,
      used: state.used,
      limit: state.limit,
      remaining: state.remaining,
      ratio: state.ratio,
      state: state.state,
      lockedIds,
      rankOf,
      rankOfOwner,
      isLocked,
      isPendingId,
      isOwnerLocked,
      isPendingOwner,
      canAdd: (n = 1) => canAdd(state, n)
    }),
    [
      status,
      isFetching,
      enforced,
      provisional,
      pending,
      state,
      lockedIds,
      rankOf,
      rankOfOwner,
      isLocked,
      isPendingId,
      isOwnerLocked,
      isPendingOwner
    ]
  )
}

/**
 * Sperrstatus eines Talents, etwa für die Detailseite per Deep Link. Lädt die
 * Talents-Query bei jedem Plan-Payment.
 *
 * pending heißt vorläufig (Entitlements nicht settled), die Talents-Query hat
 * noch keine Daten, oder das Talent fehlt in den Daten und die Query hat
 * seitdem nicht fertig geladen (isPendingId). Ein Space einer gezählten
 * Person teilt deren Rang und Sperre, auch wenn er selbst nicht zählt. Mit
 * owner gilt das auch für einen Space, der gar nicht in den Daten steht
 * (Mitarbeitende). Beispieldaten und Demo sind sofort frei, auch während
 * einer Hintergrund-Aktualisierung. Ohne Rang ist ein Talent nicht gezählt
 * und damit frei, ebenso bei einem Query-Fehler. rank ist der Rang der
 * Person.
 *
 * @param {string} spaceId
 * @param {{ owner?: string }} [options] owner des Space, sobald bekannt
 * @returns {{ status: 'pending'|'locked'|'unlocked', locked: boolean,
 *   rank: number|null, limit: number|null }}
 */
export const useTalentLock = (spaceId, { owner } = {}) => {
  const storage = useTalentStorage({ fetch: true })
  const { provisional, enforced, state, lockedIds, rankOf, limit } = storage
  const loading = storage.status === 'pending'
  const idRank = rankOf(spaceId)
  const rank = idRank ?? storage.rankOfOwner(owner)
  // Steht der Space im Index, ist seine Person bekannt, owner ändert nichts
  const lockedByRank =
    idRank !== null
      ? lockedIds.has(spaceId)
      : rank !== null && storage.isOwnerLocked(owner)
  const idPending =
    storage.isPendingId(spaceId) ||
    (!!owner && rank === null && storage.isPendingOwner(owner))

  return useMemo(() => {
    let status = TALENT_LOCK_STATUS.UNLOCKED
    if (provisional) status = TALENT_LOCK_STATUS.PENDING
    else if (!enforced || state === LIMIT_STATES.UNLIMITED) {
      status = TALENT_LOCK_STATUS.UNLOCKED
    } else if (rank !== null) {
      status = lockedByRank
        ? TALENT_LOCK_STATUS.LOCKED
        : TALENT_LOCK_STATUS.UNLOCKED
    } else if (idPending || loading) {
      status = TALENT_LOCK_STATUS.PENDING
    }

    return {
      status,
      locked: status === TALENT_LOCK_STATUS.LOCKED,
      rank,
      limit
    }
  }, [
    provisional,
    enforced,
    state,
    rank,
    lockedByRank,
    idPending,
    loading,
    limit
  ])
}

/**
 * Guard für Wiederherstellen gelöschter Talente. Blockt, wenn used + n über
 * dem Limit liegt, n ist die Zahl neuer Personen (talentRecoverDelta).
 * Gezählt wird frisch über die Talents-Query. Spaces, die schon im Speicher
 * liegen, und Spaces einer Person, die dort schon einen gezählten Space hat,
 * belegen keinen weiteren Platz. Mehrere Spaces derselben neuen Person
 * belegen einen. Zusätzlich blockt er, wenn eine heute sichtbare Person danach
 * gesperrt wäre (talentRecoverLockedVisible), etwa weil ein älterer Space eine
 * gesperrte Person nach vorn holt.
 *
 * @returns {{ checkRecover(spaces): Promise<object> }}
 */
export const useTalentRecoverGuard = () => {
  const { limits } = useEntitlements()
  const base = limits?.talentStorage ?? null
  const partnerId = usePartnerId()
  const username = useUsername()
  const queryClient = useQueryClient()

  const checkRecover = useCallback(
    (spaces) =>
      checkWithFreshCount({
        queryClient,
        resource: 'talentStorage',
        base,
        options: talentStorageUsageQueryOptions({
          partnerId,
          username,
          queryClient
        }),
        deltaOf: (data) =>
          talentRecoverDelta(
            toList(spaces),
            rankTalentStorageCached(data?.[0])
          ),
        // Kein Wiederherstellen, das eine heute sichtbare Person sperren würde
        blockedOf: (data, state) =>
          talentRecoverLockedVisible(
            toList(spaces),
            data?.[0] || [],
            state.limit
          ) > 0
      }),
    [queryClient, base, partnerId, username]
  )

  return useMemo(() => ({ checkRecover }), [checkRecover])
}

/**
 * Prüft eine Auswahl (Spaces oder IDs) auf gesperrte Talente, etwa vor
 * Kreuzvergleich, Erinnern, Zurücksetzen oder einer Teamanalyse. Nutzt den
 * Cache der Talentliste und lädt ihn in enforced nach, etwa nach einem Deep
 * Link mit Auswahl. Dazu zählt ein gesetztes storage_locked an der Zeile.
 * Gesperrt wird je Person, Zeilen derselben Person sind also beide gesperrt
 * oder beide frei. Trägt ein Eintrag owner, gilt die Sperre seiner Person
 * auch, wenn der Space selbst nicht in den Daten steht. lockedCount zählt
 * Zeilen der Auswahl.
 *
 * pending heißt, es ist noch nicht gezählt (vorläufig, ohne Daten oder eine
 * ID der Auswahl fehlt noch im Cache, während er lädt). Dann ist allowed false
 * und lockedIds enthält die noch offenen IDs, bei fehlenden Daten also jede.
 * Aufrufer warten in dem Fall und wählen nichts ab.
 *
 * @returns {{ checkSelection(spaces): { allowed: boolean, pending: boolean,
 *   lockedIds: Set<string>, lockedCount: number } }}
 */
export const useSelectionGuard = () => {
  const { isLocked, isPendingId, isOwnerLocked, isPendingOwner, pending } =
    useTalentStorage({ fetch: 'auto' })

  const checkSelection = useCallback(
    (spaces) => {
      const lockedIds = new Set()
      let selectionPending = pending
      toList(spaces).forEach((space) => {
        const isObject = !!space && typeof space === 'object'
        const id = isObject ? space.id : space
        const owner = isObject ? space.owner : null
        if (id === null || id === undefined) return
        if (isPendingId(id) || (isSetKey(owner) && isPendingOwner(owner))) {
          selectionPending = true
        }
        if (
          isLocked(id) ||
          (isSetKey(owner) && isOwnerLocked(owner)) ||
          (isObject && space.storage_locked === true)
        ) {
          lockedIds.add(id)
        }
      })

      return {
        allowed: !selectionPending && lockedIds.size === 0,
        pending: selectionPending,
        lockedIds,
        lockedCount: lockedIds.size
      }
    },
    [isLocked, isPendingId, isOwnerLocked, isPendingOwner, pending]
  )

  return useMemo(() => ({ checkSelection }), [checkSelection])
}

/**
 * Markiert die Nutzung als veraltet, nach Aktivieren, Archivieren,
 * Wiederherstellen, Discover-Aktivierung oder Löschen. Ohne resource alle
 * drei. Die Guards hängen nicht davon ab, sie zählen ohnehin frisch.
 *
 * @param {{ queryClient: object, partnerId: string,
 *   resource?: 'careers'|'seats'|'talentStorage' }} params
 */
export const invalidatePricingUsage = ({ queryClient, partnerId, resource }) =>
  Promise.all(
    (resource ? [resource] : USAGE_RESOURCES).map((key) => {
      const queryKey = usageQueryKeyPrefix(key, partnerId)
      return queryKey && partnerId
        ? queryClient.invalidateQueries({ queryKey })
        : null
    })
  )

// Nur für Tests
export const resetPricingHooksForTests = () => {
  reportedUnknown.clear()
}
